import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@viastud/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@viastud/ui/form'
import { Input } from '@viastud/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viastud/ui/select'
import { GradeEnum } from '@viastud/utils'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'

import { Benefits } from '@/components/checkout/benefits'
import { useFreeRegistrationPresenter } from '@/presenters/free-registration-presenter'

export const Route = createFileRoute('/checkout')({
  component: Checkout,
})

function Checkout() {
  const freeRegistration = useFreeRegistrationPresenter()
  const navigate = useNavigate()

  useEffect(() => {
    if (freeRegistration.success) {
      void navigate({ to: '/registration-confirmation', from: '/checkout' })
    }
  }, [freeRegistration.success, navigate])

  return (
    <div className="flex min-h-screen flex-col">
      <BackButton />
      <div className="flex grow flex-col-reverse items-center gap-10 p-4 md:flex-row md:items-start md:gap-20 md:px-20 md:py-16">
        <Benefits />
        <div className="shadow-custom flex-2 flex flex-col items-stretch gap-5 rounded-2xl px-4 py-5">
          <Form {...freeRegistration.checkoutForm}>
            <form
              className="flex w-full flex-col gap-4"
              onSubmit={freeRegistration.checkoutForm.handleSubmit((data) => {
                freeRegistration.handleFreeRegistration(data)
              })}
            >
              <FormField
                control={freeRegistration.checkoutForm.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Prénom*</FormLabel>
                    <FormControl>
                      <Input
                        className="shadow-custom w-full"
                        placeholder="Entrez votre prénom"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={freeRegistration.checkoutForm.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Nom*</FormLabel>
                    <FormControl>
                      <Input
                        className="shadow-custom w-full"
                        placeholder="Entrez votre nom de famille"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={freeRegistration.checkoutForm.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Téléphone*</FormLabel>
                    <FormControl>
                      <Input
                        className="shadow-custom w-full"
                        placeholder="06 12 34 56 78"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={freeRegistration.checkoutForm.control}
                name="grade"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Niveau*</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="shadow-custom w-full rounded-md border-gray-200">
                          <SelectValue placeholder="Sélectionnez votre niveau" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(GradeEnum)
                          .filter(([value]) => value !== 'QUATRIEME')
                          .map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {freeRegistration.freeRegistrationError && (
                <p className="text-center text-red-500">{freeRegistration.freeRegistrationError}</p>
              )}
              <Button
                className="w-full bg-blue-600 py-3 text-lg font-bold hover:bg-blue-700"
                type="submit"
              >
                Réserver votre cours offert
              </Button>
            </form>
          </Form>
        </div>
        <ViastudLogo />
      </div>
    </div>
  )
}

function ViastudLogo() {
  return (
    <div className="flex flex-col items-center gap-4 self-stretch md:hidden">
      <p className="text-3xl font-semibold text-gray-950">Rejoignez Viastud</p>
      <img
        src="/logos/viastud-text-logo.png"
        alt="viastud logo"
        className="h-auto w-40 md:hidden"
      />
    </div>
  )
}

function BackButton() {
  return (
    <div className="p-4 md:p-8">
      <Button
        variant="outline"
        className="flex items-center gap-2"
        onClick={() => (window.location.href = 'https://www.viastud.fr/')}
      >
        <ArrowLeft className="h-4 w-4" />
        Retour
      </Button>
    </div>
  )
}
